import React from 'react';

export const renderVariables = (content, organizationName, scanDate, mspInfo, clientInfo) => {
  if (!content) return [content];  // Return as array for consistency

  // Updated pattern: matches variables with optional pipe arguments (including quoted strings)
  // FIXED: Regex now correctly captures {{ variable }} format
  const variablePattern = /(\{\{\s*(?:date_today|organization_name|tenant_name|tenant\.name|date_of_scan|MSP\.user\.first_name|MSP\.user\.last_name|MSP\.logo|MSP\.large_logo|client\.name|client\.email|client\.phone_number|client\.domain)(?:\s*\|\s*(?:[^}]+))?\s*\}\})/g;
  const parts = content.split(variablePattern);

  // Helper function to format date based on pipe
  const formatDate = (dateStr, formatOrCustom) => {
    if (!dateStr) return dateStr;

    try {
      // Parse the date string (assuming DD-MM-YYYY or similar format)
      const dateParts = dateStr.split(/[-\/]/);
      let date;

      // Try to parse in different formats
      if (dateParts.length === 3) {
        // Assume DD-MM-YYYY format
        date = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
      } else {
        date = new Date(dateStr);
      }

      if (isNaN(date.getTime())) return dateStr; // Return original if invalid

      // Handle custom format string (e.g., "YYYY MM dd")
      // Remove quotes if present
      let format = formatOrCustom;
      if (format && (format.startsWith('"') || format.startsWith("'"))) {
        format = format.slice(1, -1); // Remove quotes
        
        // Custom formatting logic
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const monthsLong = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        return format
          .replace(/YYYY/g, year)
          .replace(/YY/g, String(year).slice(-2))
          .replace(/MMMM/g, monthsLong[month - 1])
          .replace(/MMM/g, monthsShort[month - 1])
          .replace(/MM/g, String(month).padStart(2, '0'))
          .replace(/M/g, month)
          .replace(/dd/g, String(day).padStart(2, '0'))
          .replace(/d/g, day);
      }

      // Handle standard presets
      switch (format) {
        case 'short':
          // 1/1/26
          return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear().toString().slice(-2)}`;
        case 'medium':
          // Jan 1, 2026
          const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
          return `${monthShort} ${date.getDate()}, ${date.getFullYear()}`;
        case 'long':
          // January 1, 2026
          const monthLong = date.toLocaleDateString('en-US', { month: 'long' });
          return `${monthLong} ${date.getDate()}, ${date.getFullYear()}`;
        default:
          // Default format with dashes: 1-1-26
          return `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear().toString().slice(-2)}`;
      }
    } catch (e) {
      return dateStr; // Return original on error
    }
  };

  return parts.map((part, index) => {
    // Safety check: skip undefined, null, or non-string parts
    if (!part || typeof part !== 'string') {
      return part;
    }

    // Handle standard variables
    if (part.match(/^\{\{\s*date_today/)) {
      // Current date
      let format = 'default';
      
      // Parse pipe argument
      const pipeMatch = part.match(/\|\s*(.+?)\s*\}\}/);
      if (pipeMatch) {
        const pipeContent = pipeMatch[1];
        // Check for quoted string (custom format)
        // Matches: "YYYY-MM-DD" or long:"YYYY-MM-DD"
        const quoteMatch = pipeContent.match(/["']([^"']+)["']/);
        if (quoteMatch) {
            // Keep quotes for formatDate to detect it's a custom format
            format = `"${quoteMatch[1]}"`; 
        } else {
            // Standard preset (short, medium, long)
            format = pipeContent.trim();
        }
      }

      const today = new Date().toLocaleDateString('en-GB'); // Use user's locale or standard format
      return <span key={index}>{formatDate(today, format)}</span>;
    } else if (part.match(/^\{\{\s*organization_name/)) {
      // If we have actual organization name (report mode), show it
      if (organizationName) {
        return <span key={index}>{organizationName}</span>;
      }
      // Otherwise show styled placeholder (template mode)
      return (
        <span
          key={index}
          style={{
            backgroundColor: 'rgba(13, 110, 253, 0.15)',
            color: '#0d6efd',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontWeight: '500',
            border: '1px dashed rgba(13, 110, 253, 0.4)'
          }}
          title="Will be replaced with organization name when generating report"
        >
          🏢 Organization Name
        </span>
      );
    } else if (part.match(/^\{\{\s*(?:tenant_name|tenant\.name)/)) {
      if (mspInfo?.tenant_name) {
        return <span key={index}>{mspInfo.tenant_name}</span>;
      }
      return (
        <span
          key={index}
          style={{
            backgroundColor: 'rgba(102, 16, 242, 0.15)',
            color: '#6610f2',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontWeight: '500',
            border: '1px dashed rgba(102, 16, 242, 0.4)'
          }}
          title="Will be replaced with the tenant name when generating report"
        >
          🏢 Tenant Name
        </span>
      );
    } else if (part.match(/^\{\{\s*date_of_scan/)) {
      // Extract format from the part if present
      let format = 'default';
      const pipeMatch = part.match(/\|\s*(.+?)\s*\}\}/);
      if (pipeMatch) {
        const pipeContent = pipeMatch[1];
        const quoteMatch = pipeContent.match(/["']([^"']+)["']/);
        if (quoteMatch) {
            format = `"${quoteMatch[1]}"`; 
        } else {
            format = pipeContent.trim();
        }
      } else {
          // Legacy/Fallback check if regex didn't catch it above (though it should)
          const formatMatch = part.match(/\|\s*(short|medium|long)/);
          if (formatMatch) format = formatMatch[1];
      }

      // If we have actual scan date (report mode), show it formatted
      if (scanDate) {
        const formattedDate = formatDate(scanDate, format);
        return <span key={index}>{formattedDate}</span>;
      }
      // Otherwise show styled placeholder (template mode)
      const placeholderText = format === 'short' ? '📅 Date (1/1/26)' :
        format === 'medium' ? '📅 Date (Jan 1, 2026)' :
          format === 'long' ? '📅 Date (January 1, 2026)' :
            '📅 Date (1-1-26)'; // Default when no pipe;
      return (
        <span
          key={index}
          style={{
            backgroundColor: 'rgba(25, 135, 84, 0.15)',
            color: '#198754',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontWeight: '500',
            border: '1px dashed rgba(25, 135, 84, 0.4)'
          }}
          title={`Will be replaced with scan date in ${format} format when generating report`}
        >
          {placeholderText}
        </span>
      );
    } else if (part.match(/^\{\{\s*MSP\.user\.first_name/)) {
      const firstName = mspInfo?.name ? mspInfo.name.split(' ')[0] : null;
      if (firstName) {
        return <span key={index}>{firstName}</span>;
      }
      return (
        <span
          key={index}
          style={{
            backgroundColor: 'rgba(102, 16, 242, 0.15)',
            color: '#6610f2',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontWeight: '500',
            border: '1px dashed rgba(102, 16, 242, 0.4)'
          }}
          title="Will be replaced with MSP user first name"
        >
          👤 MSP First Name
        </span>
      );
    } else if (part.match(/^\{\{\s*MSP\.user\.last_name/)) {
      const lastName = mspInfo?.name ? mspInfo.name.split(' ').slice(1).join(' ') : null;
      if (lastName) {
        return <span key={index}>{lastName}</span>;
      }
      return (
        <span
          key={index}
          style={{
            backgroundColor: 'rgba(102, 16, 242, 0.15)',
            color: '#6610f2',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontWeight: '500',
            border: '1px dashed rgba(102, 16, 242, 0.4)'
          }}
          title="Will be replaced with MSP user last name"
        >
          👤 MSP Last Name
        </span>
      );
    } else if (part.match(/^\{\{\s*MSP\.logo/)) {
        if (mspInfo?.tenant_logo || mspInfo?.logo) {
            return <img key={index} src={mspInfo.tenant_logo || mspInfo.logo} alt="MSP Logo" style={{ maxHeight: '50px', maxWidth: '150px', verticalAlign: 'middle' }} />;
        }
        return <img key={index} src="/images/no-image-found.png" alt="MSP Logo Fallback" style={{ maxHeight: '50px', maxWidth: '150px', verticalAlign: 'middle', opacity: 0.7 }} />;
    } else if (part.match(/^\{\{\s*MSP\.large_logo/)) {
        if (mspInfo?.tenant_large_logo || mspInfo?.large_logo) {
            return <img key={index} src={mspInfo.tenant_large_logo || mspInfo.large_logo} alt="MSP Large Logo" style={{ maxHeight: '120px', maxWidth: '400px', verticalAlign: 'middle' }} />;
        }
        // Fallback to regular logo for large logo placeholder if large is not set
        if (mspInfo?.tenant_logo || mspInfo?.logo) {
            return <img key={index} src={mspInfo.tenant_logo || mspInfo.logo} alt="MSP Logo Fallback" style={{ maxHeight: '120px', maxWidth: '400px', verticalAlign: 'middle' }} />;
        }
        return <img key={index} src="/images/no-image-found.png" alt="MSP Large Logo Fallback" style={{ maxHeight: '120px', maxWidth: '400px', verticalAlign: 'middle', opacity: 0.7 }} />;
    } else if (part.match(/^\{\{\s*client\.name/)) {
        if (clientInfo?.name) return <span key={index}>{clientInfo.name}</span>;
        return <span key={index} style={{ color: '#dc3545' }}>[Client Name Not Set]</span>;
    } else if (part.match(/^\{\{\s*client\.email/)) {
        if (clientInfo?.email) return <span key={index}>{clientInfo.email}</span>;
        return <span key={index} style={{ color: '#dc3545' }}>[Client Email Not Set]</span>;
    } else if (part.match(/^\{\{\s*client\.phone_number/)) {
        if (clientInfo?.phone) return <span key={index}>{clientInfo.phone}</span>;
        return <span key={index} style={{ color: '#dc3545' }}>[Client Phone Not Set]</span>;
    } else if (part.match(/^\{\{\s*client\.domain/)) {
        if (clientInfo?.domain) return <span key={index}>{clientInfo.domain}</span>;
        return <span key={index} style={{ color: '#dc3545' }}>[Client Domain Not Set]</span>;
    } else if (part === 'organization_name' || part === 'date_of_scan' ||
      part === 'tenant_name' || part === 'tenant.name' ||
      part === 'MSP.user.first_name' || part === 'MSP.user.last_name' || part === 'MSP.logo' || part === 'MSP.large_logo' ||
      part === 'short' || part === 'medium' || part === 'long' || (part && part.match(/^\s*\|\s*(short|medium|long)\s*$/))) {
      // Skip the captured group names and pipe syntax
      return null;
    }
    return part;
  });
};
